/**
 * Wave 11.4 — Bilateral filter fragment shader (single-pass O(K²)).
 *
 * Mirrors `src/pipeline/filters/spatial/bilateral.js` exactly:
 *
 *   B(p) = (1/W) · Σ_q G_σs(|p − q|) · G_σr(|L_p − L_q|) · I_q
 *   W    =        Σ_q G_σs(|p − q|) · G_σr(|L_p − L_q|)
 *
 * where L is Rec.709 luminance of the centre vs neighbour. Range
 * distance is one scalar per pixel (not three) — same trick the CPU
 * path uses to keep colour drift low at edges. Spatial weight is
 * pre-computed per (dx, dy) into `uSpatial[(dy+r)*(2r+1) + (dx+r)]`,
 * a flat float array.
 *
 * K is capped at σs ≤ 5 → r ≤ 15 → K ≤ 31 (matches CPU
 * `BILATERAL_SIGMA_SPATIAL_MAX`). Compile-time KMAX = 15 keeps the
 * uniform spatial-kernel storage to 31² = 961 floats — comfortably
 * under the per-program uniform budget. r is uploaded via uRadius and
 * the loop bails at j > r.
 *
 * Boundary: mirror-extend via `mirrorIdx(i, n)` (same as Gaussian
 * shader, same as CPU `_util.js::mirrorIndex`).
 *
 * References:
 *   • Tomasi & Manduchi (1998) "Bilateral Filtering for Gray and Color
 *     Images", ICCV 1998.
 */

/* Compile-time max half-kernel radius. σs ≤ 5 means r ≤ ceil(3·5) = 15. */
export const BILATERAL_KMAX = 15;
/* Square kernel size at KMAX — used by host to allocate the spatial
 * uniform array. */
export const BILATERAL_KSQ_MAX = (2 * BILATERAL_KMAX + 1) * (2 * BILATERAL_KMAX + 1);

export const FRAG_BILATERAL = /* glsl */ `#version 300 es
precision highp float;
precision highp int;

uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;

/* Pre-computed spatial Gaussian, flat row-major of size (2r+1)². */
uniform float uSpatial[${BILATERAL_KSQ_MAX}];
uniform int   uRadius;        // r ∈ [1, KMAX]
uniform float uInv2SigmaR2;   // 1 / (2 · σ_range²)

in vec2 vUv;
out vec4 outColor;

const float LUMA_R = 0.2126;
const float LUMA_G = 0.7152;
const float LUMA_B = 0.0722;

/* Mirror-extend index — single modulo into period 2(n-1) so the
 * result is correct for ARBITRARILY-far-out indices (matches CPU
 * _util.js::mirrorIndex exactly). GLSL modulo "%" is implementation-
 * defined for negative operands; we normalise via add-then-mod. */
int mirrorIdx(int x, int n) {
    if (n <= 1) return 0;
    int period = 2 * (n - 1);
    int m = x % period;
    if (m < 0) m += period;
    return m < n ? m : period - m;
}

void main() {
    int xc = int(floor(vUv.x * float(uSrcSize.x)));
    int yc = int(floor(vUv.y * float(uSrcSize.y)));
    if (xc >= uSrcSize.x) xc = uSrcSize.x - 1;
    if (yc >= uSrcSize.y) yc = uSrcSize.y - 1;

    vec4 centre = texelFetch(uSrc, ivec2(xc, yc), 0);
    float cL = LUMA_R * centre.r + LUMA_G * centre.g + LUMA_B * centre.b;

    int K = 2 * uRadius + 1;
    vec3 sumC = vec3(0.0);
    float sumW = 0.0;

    /* Compile-time bound for outer loops — break when |dy|/|dx| > r. */
    for (int dy = -${BILATERAL_KMAX}; dy <= ${BILATERAL_KMAX}; dy++) {
        if (dy < -uRadius) continue;
        if (dy >  uRadius) break;
        int ny = mirrorIdx(yc + dy, uSrcSize.y);
        for (int dx = -${BILATERAL_KMAX}; dx <= ${BILATERAL_KMAX}; dx++) {
            if (dx < -uRadius) continue;
            if (dx >  uRadius) break;
            int nx = mirrorIdx(xc + dx, uSrcSize.x);
            vec4 s = texelFetch(uSrc, ivec2(nx, ny), 0);
            float sL = LUMA_R * s.r + LUMA_G * s.g + LUMA_B * s.b;
            float dL = sL - cL;
            float wS = uSpatial[(dy + uRadius) * K + (dx + uRadius)];
            float wR = exp(-dL * dL * uInv2SigmaR2);
            float w  = wS * wR;
            sumC += s.rgb * w;
            sumW += w;
        }
    }

    /* alpha unchanged — preserves the editor's canonical α = 1.0. */
    vec3 outRGB = (sumW > 0.0) ? (sumC / sumW) : centre.rgb;
    outColor = vec4(outRGB, centre.a);
}
`;
